import React, { useState, useRef, useEffect } from 'react'
import {
	Box,
	Text,
	Spinner,
	Center,
	useDisclosure,
	Portal,
	Flex,
	GridItem,
	Grid,
	useOutsideClick,
} from '@chakra-ui/react'
import { useLanguageContext } from '../../../context/locale.context'
import { useQuery } from 'react-query'
import { getCollectionV2QueryKey } from './../../../utils/queryKeyFactory'
import { useAuthContext } from '../../../context/auth.context'
import { managerDetailsQuery } from './../../../hooks/dashboard.hooks'
import SVGIcon from './../../../assets/svgIcons/index'
import { Collapse } from '@chakra-ui/transition'
import { AiOutlineClose } from 'react-icons/ai'
import { BsCameraVideoOff } from 'react-icons/bs'

function WorkStatus({ onVideoPlayListner }) {
	const {
		state: { locale },
	} = useLanguageContext()
	const {
		state: { authData },
	} = useAuthContext()

	const managerDetailsParam = {
		manager_id: authData.asset_id,
	}

	const { isOpen, onToggle } = useDisclosure()

	const [isVideoOn, setIsVideoOn] = useState(true)

	const videoRef = useRef(null)

	const {
		data: managerDetailsData,
		refetch,
		isLoading: loadingManagerDetails,
	} = useQuery(
		getCollectionV2QueryKey.asset_param(managerDetailsParam),
		() => managerDetailsQuery(managerDetailsParam),
		{
			staleTime: 60 * 1000,
			refetchOnMount: true,
			enabled: !!managerDetailsParam.manager_id,
		}
	)

	useEffect(() => {
		!!navigator.mediaDevices &&
			navigator.mediaDevices
				.getUserMedia({
					video: true,
				})
				.then(function (stream) {
					setIsVideoOn(true)
				})
				.catch(function (err) {
					setIsVideoOn(false)
				})
	}, [])

	useOutsideClick({
		ref: videoRef,
		handler: () => {
			if (isOpen) onToggle()
		},
	})

	const renderVideo = () => {
		if (isVideoOn) {
			return (
				<>
					<video
						width='100%'
						height='25rem'
						style={{
							borderRadius: '10px',
							mixBlendMode: 'screen',
							filter: 'url(#line-art) url(#sharpen-filter)',
						}}
						autoPlay
						muted
						onPlay={onVideoPlayListner}
						id='userCameraView'
					></video>

					<svg height='0'>
						<defs>
							<filter
								width='100'
								height='100'
								id='line-art'
								color-interpolation-filters='sRGB'
							>
								<feColorMatrix
									in='SourceGraphic'
									type='matrix'
									values='-1 0 0 0 1
                                                              0 -1 0 0 1
                                                              0 0 -1 0 1
                                                              0 0 0 1 0'
									result='inverted'
								/>
								<feGaussianBlur
									id='gaussianBlur'
									in='inverted'
									stdDeviation='10'
									result='inverted-blurred'
								/>
								<feBlend
									in='SourceGraphic'
									in2='inverted-blurred'
									mode='color-dodge'
									result='colorDodge'
								/>

								<feColorMatrix type='saturate' values='0' />
							</filter>
							<filter id='sharpen-filter'>
								<feConvolveMatrix
									id='sharpen'
									order='3'
									kernelMatrix='-1 -1 -1 -1 8.5 -1 -1 -1 -1'
								/>
							</filter>
						</defs>
					</svg>
				</>
			)
		} else
			return (
				<Center w='100%' h='25rem' bg='gray.700' fontSize='1.2rem'>
					<Text color='gray.400'>Video Not Enabled</Text>
					<BsCameraVideoOff
						style={{
							width: '2rem',
							height: '2rem',
							marginLeft: '1rem',
						}}
					/>
				</Center>
			)
	}

	if (loadingManagerDetails)
		return (
			<Center>
				<Spinner color='brand.900' />
			</Center>
		)

	return (
		<>
			<Flex
				position='relative'
				bg={isOpen && 'white'}
				h='100%'
				alignItems='center'
				px='0.5rem'
			>
				<Box cursor='pointer' onClick={onToggle}>
					<SVGIcon
						iconName='RecordSvg'
						svgProps={{
							width: '32',
							height: '32',
							viewBox: '0 0 32 32',
						}}
						pathProps={isOpen && { fill: '#303030' }}
					/>
				</Box>
				<Portal>
					<Box
						display={isOpen ? 'block' : 'none'}
						position='fixed'
						w='100vw'
						h='100vh'
						inset='0'
						bg='rgba(0, 0, 0, 0.8)'
						cursor='default'
					></Box>
				</Portal>

				<Collapse
					in={isOpen}
					animateOpacity
					style={{ position: 'absolute', top: '100%', right: '0' }}
				>
					<Box
						maxH='70vh'
						minW='44rem'
						overflowY='auto'
						bg='white'
						borderRadius='0px 0px 15px 15px'
						w='fit-content'
						padding='1.1rem 2rem 2rem 2rem'
						fontSize='0.875rem'
						fontWeight='900'
						ref={videoRef}
					>
						<AiOutlineClose
							style={{
								margin: '0 0 2rem auto',
								color: '#EF3D3D',
								width: '1.2rem',
								height: '1.2rem',
								cursor: 'pointer',
							}}
							onClick={onToggle}
						/>
						<Box overflow='hidden' bg='#0047AB' borderRadius='10px'>
							{renderVideo()}
						</Box>
						<Text fontSize='1.25rem' alignSelf='flex-end'>
							{locale['Work Status']}
						</Text>

						<Grid
							templateColumns='repeat(3, auto)'
							gridColumnGap='1rem'
							mt='1rem'
						>
							<GridItem>
								<Text color='brand.900' fontWeight='400'>
									{locale['Availability']}
								</Text>
							</GridItem>
							<GridItem>:</GridItem>
							<GridItem>
								<Text>{locale['NA']}</Text>
							</GridItem>
							<GridItem>
								<Text color='brand.900' fontWeight='400'>
									{locale['Designated Location']}
								</Text>
							</GridItem>
							<GridItem>:</GridItem>
							<GridItem>
								<Text>
									{managerDetailsData?.asset_work_location_address ||
										locale['NA']}
								</Text>
							</GridItem>
							<GridItem>
								<Text color='brand.900' fontWeight='400'>
									{locale['Last known location']}
								</Text>
							</GridItem>
							<GridItem>:</GridItem>
							<GridItem>
								<Text>
									{managerDetailsData?.asset_last_location_address ||
										locale['NA']}
								</Text>
							</GridItem>
						</Grid>
					</Box>
				</Collapse>
			</Flex>
		</>
	)
}

export default WorkStatus
